import { App, Button } from 'antd';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getListAdvice,
  UpdateAdvice,
  watchListAdvice
} from '@/redux/slices/adivce-recommend.slice';
import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import AdviceRecommendTabDevices from '@/components/tabs/advice-recommend.device';
import CustomCollapse from '@/components/CustomCollapse';
import { AdviceDetail } from '@/types/advice';
import type { FormInstance } from 'antd/es/form/Form';
import DetailFormItem from '@/components/forms/advice-recommend/advice-recommend.CK.form.tsx';
import { setMessages } from '@/redux/slices/app.slice';
import { Message } from '@/enums/message.enum';
import { ExclamationCircleOutlined } from '@ant-design/icons';

type AdviceRecommendDietProps = {
  filterValue: FilterAdviceRecommend;
};

export default function AdviceRecommendDiet({
  filterValue
}: AdviceRecommendDietProps) {
  const dispatch = useAppDispatch();
  const collapseContent = useAppSelector(watchListAdvice);
  const { modal } = App.useApp();
  const [activeTab, setActiveTab] = useState<'PC' | 'MOBILE'>('PC');
  const [activeCollapse, setActiveCollapse] = useState<number[]>([]);
  const [editedDetails, setEditedDetails] = useState<
    Record<number, AdviceDetail>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const resetEdit = useRef(1);
  const [isAdjust, setIsAdjust] = useState(false);

  const formRefs = useRef<Record<number, FormInstance>>({});

  useEffect(() => {
    if (!filterValue.ageRange) {
      return;
    }

    setEditedDetails({});
    setActiveCollapse([]);

    dispatch(
      getListAdvice({
        ageRangeId: filterValue.ageRange,
        adviceCateCode: 'DIET',
        platform: activeTab
      })
    );
  }, [dispatch, filterValue.ageRange, activeTab]);

  const handleDetailChange = (updatedDetail: AdviceDetail) => {
    setEditedDetails((prev) => ({
      ...prev,
      [updatedDetail.ID]: updatedDetail
    }));
  };
  const handleResetAdjust = () => {
    setEditedDetails({});
    setIsAdjust(false);
    resetEdit.current += 1;
  };

  const handleFormMount = (form: FormInstance, id: number) => {
    formRefs.current[id] = form;
  };

  const handleSaveAll = async () => {
    try {
      setIsLoading(true);
      const updates = Object.values(editedDetails);

      await Promise.all(updates.map((d) => dispatch(UpdateAdvice(d)))).then(
        (res) => {
          if (res.every((r) => r.meta.requestStatus === 'fulfilled')) {
            dispatch(
              setMessages([
                {
                  type: Message.SUCCESS,
                  message: 'Lưu thông tin thay đổi thành công'
                }
              ])
            );
            dispatch(
              getListAdvice({
                ageRangeId: filterValue.ageRange,
                adviceCateCode: 'DIET',
                platform: activeTab
              })
            );
          } else {
            dispatch(
              setMessages([
                {
                  type: Message.ERROR,
                  message:
                    'Có lỗi xảy ra khi lưu thông tin. Vui lòng thử lại sau.'
                }
              ])
            );
          }
        }
      );
      setEditedDetails({});
    } catch (err) {
      console.warn('Có lỗi khi lưu:', err);
      dispatch(
        setMessages([
          {
            type: Message.ERROR,
            message: 'Có lỗi xảy ra khi lưu thông tin. Vui lòng thử lại sau.'
          }
        ])
      );
    } finally {
      setIsLoading(false);
      setIsAdjust(false);
    }
  };

  const confirmAdjust = async () => {
    try {
      modal.confirm({
        title: 'Lưu thay đổi',
        content: (
          <div css={containerModalConfirm}>
            <em>
              Bạn có chắc chắn muốn lưu các thông tin vừa điều chỉnh hay không?
            </em>
            <em>Lưu ý: Hành động này không thể hoàn tác</em>
          </div>
        ),
        icon: <ExclamationCircleOutlined />,
        closable: true,
        okText: 'Xác nhận',
        width: 450,
        cancelText: 'Hủy',
        onOk: () => handleSaveAll()
      });
    } catch (err) {
      console.warn('Có lỗi validate:', err);
      dispatch(
        setMessages([
          {
            type: Message.ERROR,
            message:
              'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại trước khi lưu.'
          }
        ])
      );
    }
  };

  const validateForm = async () => {
    try {
      const validationPromises = Object.values(formRefs.current).map((form) =>
        form.validateFields().catch((err) => err)
      );

      const results = await Promise.all(validationPromises);

      const hasErrors = results.some(
        (result) => result?.errorFields?.length > 0
      );

      if (hasErrors) {
        dispatch(
          setMessages([
            {
              type: Message.ERROR,
              message: 'Vui lòng nhập đầy đủ thông tin bắt buộc'
            }
          ])
        );
        return;
      }

      confirmAdjust();
    } catch (err) {
      console.warn('Có lỗi validate:', err);
      dispatch(
        setMessages([
          {
            type: Message.ERROR,
            message:
              'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại trước khi lưu.'
          }
        ])
      );
    }
  };

  return (
    <div css={boxStyles}>
      <AdviceRecommendTabDevices
        activeKey={activeTab}
        onChange={setActiveTab}
      />

      <div css={contentStyles}>
        {collapseContent.map((item) => (
          <CustomCollapse
            key={item.ADVICE_ID}
            title={item.ADVICE_TITLE}
            defaultActive={activeCollapse.includes(item.ADVICE_ID)}
            onChange={() =>
              setActiveCollapse((prev) =>
                prev.includes(item.ADVICE_ID)
                  ? prev.filter((id) => id !== item.ADVICE_ID)
                  : [...prev, item.ADVICE_ID]
              )
            }
          >
            {item.DETAILS.map((detail, index) => (
              <DetailFormItem
                key={`${detail.ID} ${resetEdit.current}`}
                detail={detail}
                onUpdateDetail={handleDetailChange}
                onMount={handleFormMount}
                index={index}
                isAdjust={isAdjust}
                editingData={editedDetails}
              />
            ))}
          </CustomCollapse>
        ))}

        {!isAdjust && (
          <div css={saveWrapper}>
            <Button
              loading={isLoading}
              type='primary'
              onClick={() => setIsAdjust(true)}
            >
              Chỉnh sửa
            </Button>
          </div>
        )}

        {isAdjust && (
          <div css={saveWrapper}>
            <Button type='primary' onClick={handleResetAdjust}>
              Huỷ{' '}
            </Button>
            <Button
              loading={isLoading}
              type='primary'
              onClick={validateForm}
              disabled={!Object.keys(editedDetails).length}
            >
              Lưu thay đổi
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const saveWrapper = css`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  gap: 1rem;
`;

const boxStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.4rem 2.4rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
`;

const contentStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .ant-collapse {
    background-color: transparent;
    border: none;

    .ant-collapse-item {
      border: none;

      .ant-collapse-header {
        background-color: #ebebeb;
        border-radius: 4px;
      }
    }
  }
`;

const containerModalConfirm = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  em {
    font-style: normal;
    font-weight: 500;
  }
`;
